import { useState } from "react";
import { ISentinelRich } from "../../Models/ISentinel";
import { ISensor } from "../../Models/ISensor";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import {
  buildCameraUrl,
  DEFAULT_SENSORS,
  DeviceKind,
  devLabel,
  dispName,
  filled,
  hasPerson,
  ICameraFields,
  ipError,
  kindOf,
  parseCameraUrl,
  portError,
  scattaText,
  SENSOR_DEFS,
  SENSOR_ORDER,
  sensorGivesAlarms,
  sensorTitle,
} from "./SetupHelpers";
import { Btn, BtnWhy, Dialog, Field, Msg, Pill, Switch, TextInput } from "./SetupUi";

// Finestre della riga di un dispositivo (risposta 1 = una matita per colonna): ognuna salva solo la sua
// parte, con le rotte di oggi. «new» crea il dispositivo solo alla conferma (risposta 2).
//   new      POST /sentinels, poi PUT /patients/:id e PUT /sentinels/:id (tipo, telecamera vuota)
//   name     PUT /sentinels/:id (tipo) e PUT /patients/:id
//   sensors  PUT /sentinels/:sentinelId/:sensorId e POST /sensors/createOrEdit: nessun sensore si cancella
//   cam      PUT /sentinels/:id (cameraUrl)
//   audio    PUT /sentinels/:id (audioEnabled)

export type DevicePart = "new" | "name" | "sensors" | "cam" | "audio";

interface ISensorDraft {
  id?: number;
  type: string;
  th: string;
  en: boolean;
  min: string;
  max: string;
  added?: boolean;
}

interface IDraft {
  kind: DeviceKind;
  person: { identifier: string; floor: string; room: string; bed: string };
  cam: ICameraFields;
  audio: boolean;
  sensors: ISensorDraft[];
}

const val = (v: any) => (filled(v) ? String(v) : "");

const newSensor = (type: string, en: boolean): ISensorDraft => ({
  type,
  th: String(SENSOR_DEFS[type]?.threshold ?? 0),
  en,
  min: String(SENSOR_DEFS[type]?.range[0] ?? 0),
  max: String(SENSOR_DEFS[type]?.range[1] ?? 0),
  added: true,
});

const draftOf = (s: ISentinelRich | undefined, kind: DeviceKind): IDraft => {
  if (!s) {
    return {
      kind,
      person: { identifier: "", floor: "", room: "", bed: "" },
      cam: parseCameraUrl(""),
      audio: false,
      sensors: DEFAULT_SENSORS[kind].map((t) => newSensor(t, false)),
    };
  }
  const p = s.patient;
  return {
    kind: kindOf(s),
    person: { identifier: val(p?.identifier), floor: val(p?.floor), room: val(p?.room), bed: val(p?.bed) },
    cam: parseCameraUrl(s.cameraUrl),
    audio: !!s.audioEnabled,
    sensors: [...(s.sensors ?? [])]
      .sort((a, b) => SENSOR_ORDER.indexOf(a.type) - SENSOR_ORDER.indexOf(b.type))
      .map((x: ISensor) => ({
        id: x.id,
        type: x.type,
        th: String(x.threshold ?? ""),
        en: !!x.enabled,
        min: String(x.startThreshold ?? ""),
        max: String(x.endThreshold ?? ""),
      })),
  };
};

const isNum = (v: string) => String(v).trim() !== "" && !isNaN(+v);
const noSemicolon = (v: string) => v.replace(/;/g, "");
const dash = (v: string) => (v.trim() === "" ? "-" : v.trim());

const DeviceDialog = (props: {
  part: DevicePart;
  sentinel?: ISentinelRich;
  onClose: () => void;
  // dopo il salvataggio: messaggio per l'avviso e id del dispositivo da ricaricare
  onSaved: (message: string, sentinelId?: number) => void;
}) => {
  const s = props.sentinel;
  const part = props.part;
  const [orig] = useState<IDraft>(() => draftOf(s, "Letto"));
  const [d, setD] = useState<IDraft>(() => draftOf(s, "Letto"));
  const [saving, setSaving] = useState<boolean>(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [showPw, setShowPw] = useState<boolean>(false);
  const [adv, setAdv] = useState<Record<number, boolean>>({});
  const [newSens, setNewSens] = useState<string>("");

  const set = (patch: Partial<IDraft>) => setD((prev) => ({ ...prev, ...patch }));
  const setPerson = (k: keyof IDraft["person"], v: string) => setD((prev) => ({ ...prev, person: { ...prev.person, [k]: v } }));
  const setCam = (k: keyof ICameraFields, v: string) => setD((prev) => ({ ...prev, cam: { ...prev.cam, [k]: noSemicolon(v) } }));
  const setSensor = (i: number, patch: Partial<ISensorDraft>) =>
    setD((prev) => ({ ...prev, sensors: prev.sensors.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));

  const kindChanged = !!s && d.kind !== orig.kind;
  const camTouched = !!(d.cam.ip.trim() || d.cam.port.trim() || d.cam.user || d.cam.password || d.cam.channel.trim());
  const camErr = part === "cam" && camTouched ? ipError(d.cam.ip) || portError(d.cam.port) : null;
  const badSensor =
    part === "sensors"
      ? d.sensors.find(
          (x) =>
            (sensorGivesAlarms(x.type) && x.type !== "door" && !isNum(x.th)) ||
            !isNum(x.min) ||
            !isNum(x.max) ||
            +x.min > +x.max
        )
      : undefined;
  const changed = !s || JSON.stringify(d) !== JSON.stringify(orig);
  // dispositivi vecchi senza persona collegata: nome e posizione non hanno dove essere scritti
  const noPatient = part === "name" && !!s && !s.patient;
  const why = saving
    ? "Salvataggio in corso..."
    : noPatient
    ? "Questo dispositivo non ha una persona collegata: si imposta da Configurazione."
    : badSensor
    ? `Correggi i valori di «${sensorTitle(badSensor.type)}».`
    : camErr
    ? "Correggi la telecamera: " + camErr + "."
    : !changed
    ? "Nessuna modifica da salvare."
    : null;

  // ---------------- salvataggio ----------------

  const fail = (text: string) => {
    setSaving(false);
    setSaveErr(text);
  };

  const savePatient = async (patientId: number) => {
    const p = d.person;
    const r: IAPIResponse = await ApiService.PatientController.UpdatePatient({
      id: patientId,
      identifier: dash(p.identifier),
      floor: dash(p.floor),
      room: dash(p.room),
      bed: d.kind === "Letto" ? dash(p.bed) : "-",
    } as any);
    return r.error === null;
  };

  // solo la parte cambiata: il server non tocca gli altri campi (Attivo compreso)
  const saveSentinel = async (id: number, patch: any) => {
    const r: IAPIResponse = await ApiService.SentinelController.UpdateSentinel({ id, ...patch });
    return r.error === null;
  };

  const save = async () => {
    if (why) return;
    setSaving(true);
    setSaveErr(null);
    try {
      await saveParts();
    } catch (e) {
      fail("Salvataggio non riuscito. Riprova.");
    }
  };

  const saveParts = async () => {
    if (part === "new") {
      const created: IAPIResponse = await ApiService.SentinelController.AddSentinel(d.kind);
      if (created.error !== null || !created.parsed) return fail("Il server non ha creato il dispositivo. Riprova.");
      const c = created.parsed;
      // il server mette una telecamera di esempio ai letti nuovi: la telecamera si aggiunge con la sua matita
      const okSentinel = await saveSentinel(c.id, { identifier: d.kind, cameraUrl: "" });
      const p = d.person;
      const okPatient = filled(p.identifier) || filled(p.floor) || filled(p.room) || filled(p.bed) ? await savePatient(c.patientId) : true;
      if (!okPatient) {
        props.onSaved(d.kind + " n. " + c.id + " creato, ma nome e posizione non sono stati salvati: riprova con la matita della riga.", c.id);
        return;
      }
      if (!okSentinel) {
        props.onSaved(d.kind + " n. " + c.id + " creato, ma è rimasta la telecamera di esempio del server: toglila con la matita della Telecamera.", c.id);
        return;
      }
      props.onSaved(d.kind + " n. " + c.id + " creato: aggiungi operatori e telecamera dalla riga.", c.id);
      return;
    }
    if (!s) return fail("Il dispositivo non c'è più: chiudi e ricarica la pagina.");
    if (part === "name") {
      if (kindChanged && !(await saveSentinel(s.id, { identifier: d.kind }))) return fail("Il tipo non è stato salvato. Riprova.");
      if (!(await savePatient(s.patient.id))) return fail("Nome e posizione non sono stati salvati. Riprova.");
      props.onSaved(dispNameAfter() + ": nome e posizione salvati.", s.id);
      return;
    }
    if (part === "cam") {
      if (!(await saveSentinel(s.id, { cameraUrl: buildCameraUrl(d.cam) }))) return fail("La telecamera non è stata salvata. Riprova.");
      props.onSaved(dispName(s) + ": telecamera salvata.", s.id);
      return;
    }
    if (part === "audio") {
      if (!(await saveSentinel(s.id, { audioEnabled: d.audio }))) return fail("L'audio non è stato salvato. Riprova.");
      props.onSaved(dispName(s) + (d.audio ? ": audio attivato." : ": audio spento."), s.id);
      return;
    }
    // sensori: uno per uno, solo quelli cambiati
    const failed: string[] = [];
    for (let i = 0; i < d.sensors.length; i++) {
      const x = d.sensors[i];
      const o = orig.sensors.find((y) => y.id !== undefined && y.id === x.id);
      if (x.id === undefined) {
        // nasce spento (il server lo crea con soglia 0); soglia e Attivo si scrivono insieme subito dopo:
        // se la seconda chiamata non riesce resta spento, mai acceso con la soglia sbagliata
        const r: IAPIResponse = await ApiService.SensorController.EditOrCreateSensor({
          sentinelId: s.id,
          type: x.type,
          enabled: false,
          startThreshold: +x.min,
          endThreshold: +x.max,
          postAlarmScript: "",
        } as any);
        if (r.error !== null || !r.parsed) {
          failed.push(sensorTitle(x.type));
          continue;
        }
        const r2: IAPIResponse = await ApiService.SentinelController.UpdateSensor(String(s.id), {
          id: r.parsed.id,
          enabled: x.en,
          ...(x.type === "door" ? {} : { threshold: +x.th }),
        } as any);
        if (r2.error !== null) failed.push(sensorTitle(x.type) + " (creato spento)");
        continue;
      }
      if (o && JSON.stringify(o) === JSON.stringify(x)) continue;
      const r: IAPIResponse = await ApiService.SentinelController.UpdateSensor(String(s.id), {
        id: x.id,
        enabled: x.en,
        // la porta usa la soglia per la batteria: la scrive il dispositivo, qui non si manda
        ...(x.type === "door" ? {} : { threshold: +x.th }),
        startThreshold: +x.min,
        endThreshold: +x.max,
      } as any);
      if (r.error !== null) failed.push(sensorTitle(x.type));
    }
    if (failed.length > 0) {
      // quelli riusciti sono salvati: si ricarica la riga e la finestra dice cosa manca
      props.onSaved("Non salvati: " + failed.join(", ") + ". Riprova con la matita dei Sensori.", s.id);
      return;
    }
    props.onSaved(dispName(s) + ": sensori salvati.", s.id);
  };

  const dispNameAfter = () => (filled(d.person.identifier) ? d.person.identifier.trim() : s ? dispName(s) : "");

  // ---------------- parti della finestra ----------------

  const tipo = (
    <div className="fld">
      <label htmlFor="dev-kind">Tipo</label>
      <select
        id="dev-kind"
        className="tf"
        style={{ maxWidth: 240 }}
        value={d.kind}
        onChange={(e) => {
          const kind = e.target.value as DeviceKind;
          // nuovo: nascono solo i sensori del tipo scelto
          set(s ? { kind } : { kind, sensors: DEFAULT_SENSORS[kind].map((t) => newSensor(t, false)) });
        }}
      >
        <option value="Letto">Letto</option>
        <option value="Varco">Varco</option>
      </select>
      {kindChanged ? (
        <span className="hint" style={{ color: "#484644" }}>
          I sensori restano quelli di prima: si cambiano con la matita di Sensori.
        </span>
      ) : (
        <span className="hint">
          {s ? "Se ci si è sbagliati, un letto può diventare un varco e viceversa." : "Decide quali sensori nascono con il dispositivo."}
        </span>
      )}
    </div>
  );

  const person = (
    <div className="sec">
      <div className={d.kind === "Letto" ? "grid3" : "grid3 nobed"}>
        <Field
          label="Nome (come compare in app)"
          hint={d.kind === "Varco" ? "Per un varco è il nome della porta." : "Per un letto, di solito il nome della persona o del letto."}
        >
          <TextInput
            value={d.person.identifier}
            autoFocus
            placeholder={d.kind === "Varco" ? "es. Ingresso principale" : "es. Letto 2"}
            onChange={(v) => setPerson("identifier", v)}
          />
        </Field>
        <Field label="Zona">
          <TextInput value={d.person.floor} onChange={(v) => setPerson("floor", v)} />
        </Field>
        <Field label="Stanza">
          <TextInput value={d.person.room} onChange={(v) => setPerson("room", v)} />
        </Field>
        {d.kind === "Letto" && (
          <Field label="Letto">
            <TextInput value={d.person.bed} onChange={(v) => setPerson("bed", v)} />
          </Field>
        )}
      </div>
      {!filled(d.person.identifier) && (
        <span className="sub">Senza nome, nell'app e nelle email compare tipo e numero del dispositivo (es. «Letto n. 36»).</span>
      )}
    </div>
  );

  const sensors = () => {
    // ogni dispositivo può avere sensori di ogni genere (decisione del titolare, 24/10/2026): nessun abbinamento è un errore
    const missing = SENSOR_ORDER.filter((k) => !d.sensors.some((x) => x.type === k));

    const row = (x: ISensorDraft, i: number) => {
      const def = SENSOR_DEFS[x.type];
      const alarms = sensorGivesAlarms(x.type) && x.type !== "door";
      const badTh = alarms && !isNum(x.th);
      const badRange = !isNum(x.min) || !isNum(x.max) || +x.min > +x.max;
      const open = adv[i] !== undefined ? adv[i] : badTh || badRange;
      return (
        <div key={i}>
          <div className={"sline" + (open ? " open" : "")}>
            <div className="grow">
              <b>{sensorTitle(x.type)}</b>
              <span className="sub" style={{ display: "block" }}>
                {scattaText(x.type, x.th)}
              </span>
              {(badTh || badRange || (x.type === "pesogr" && isNum(x.th) && +x.th === 0)) && (
                <span className="swarn">
                  {x.type === "pesogr" && isNum(x.th) && +x.th === 0 && (
                    <Pill kind="warn" text="Con soglia 0 non scatta mai" title="La soglia la corregge chi conosce il letto (o la tara in Configurazione)." />
                  )}
                  {badTh ? <span className="err">La soglia deve essere un numero</span> : badRange ? <span className="err">Minimo e massimo da correggere</span> : null}
                </span>
              )}
            </div>
            <Switch
              on={x.en}
              label={x.en ? "Attivo" : "Spento"}
              onClick={() => setSensor(i, { en: !x.en })}
            />
            <Btn
              icon={open ? "ChevronDown" : "ChevronRight"}
              className="chev"
              title={open ? "Chiudi Soglia, Minimo, Massimo" : "Avanzate: Soglia, Minimo, Massimo"}
              onClick={() => setAdv({ ...adv, [i]: !open })}
            />
          </div>
          {open && (
            <div className="sadv">
              <div className="grid3 nobed" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                {x.type === "door" || !sensorGivesAlarms(x.type) ? (
                  <div className="fld">
                    <span className="lab">{x.type === "door" ? "Batteria" : "Soglia"}</span>
                    <span className="sub" style={{ lineHeight: "32px" }}>
                      {x.type === "door" ? x.th + " · la scrive il dispositivo" : "nessuna"}
                    </span>
                  </div>
                ) : (
                  <Field label="Soglia">
                    <TextInput value={x.th} bad={badTh} ariaLabel={"Soglia di " + sensorTitle(x.type)} onChange={(v) => setSensor(i, { th: v })} />
                  </Field>
                )}
                <Field label="Minimo">
                  <TextInput value={x.min} bad={badRange} onChange={(v) => setSensor(i, { min: v })} />
                </Field>
                <Field label="Massimo">
                  <TextInput value={x.max} bad={badRange} onChange={(v) => setSensor(i, { max: v })} />
                </Field>
              </div>
              {def && <span className="sub">{def.desc}</span>}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="sec">
        <div className="slist">
          {d.sensors.map((x, i) => row(x, i))}
          {d.sensors.length === 0 && <span className="sub">Nessun sensore.</span>}
        </div>
        {missing.length > 0 && (
          <div className="cellrow" style={{ alignItems: "flex-start" }}>
            <select className="tf" style={{ flex: 1 }} value={newSens} aria-label="Aggiungi un sensore" onChange={(e) => setNewSens(e.target.value)}>
              <option value="">Aggiungi un sensore...</option>
              {missing.map((k) => (
                <option key={k} value={k}>
                  {sensorTitle(k)}
                  {sensorGivesAlarms(k) ? "" : " (misura, non dà allarmi)"}
                </option>
              ))}
            </select>
            <BtnWhy
              text="Aggiungi"
              why={newSens ? null : "Scegli prima il sensore"}
              onClick={() => {
                set({ sensors: [...d.sensors, newSensor(newSens, true)] });
                setNewSens("");
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const camera = (
    <div className="sec">
      <div className="grid2">
        <Field label="Indirizzo IP" err={camErr ? ipError(d.cam.ip) : null}>
          <TextInput value={d.cam.ip} placeholder="192.168.1.20" bad={!!(camErr && ipError(d.cam.ip))} autoFocus onChange={(v) => setCam("ip", v)} />
        </Field>
        <Field label="Porta" err={camErr ? portError(d.cam.port) : null}>
          <TextInput value={d.cam.port} placeholder="554" bad={!!(camErr && portError(d.cam.port))} onChange={(v) => setCam("port", v)} />
        </Field>
        <Field label="Utente">
          <TextInput value={d.cam.user} onChange={(v) => setCam("user", v)} />
        </Field>
        <Field label="Password">
          <div className="pw">
            <TextInput value={d.cam.password} type={showPw ? "text" : "password"} onChange={(v) => setCam("password", v)} />
            <Btn icon={showPw ? "Hide3" : "RedEye"} title={showPw ? "Nascondi la password" : "Mostra la password"} onClick={() => setShowPw(!showPw)} />
          </div>
        </Field>
        <Field label="Canale">
          <TextInput value={d.cam.channel} placeholder="1" onChange={(v) => setCam("channel", v)} />
        </Field>
      </div>
      <span className="sub">Il carattere ";" non si può usare nei campi.</span>
      {d.cam.ip && hasSavedCam() && (
        <div>
          <Btn text="Togli telecamera" onClick={() => set({ cam: parseCameraUrl("") })} />
        </div>
      )}
    </div>
  );

  // modulo vocale: un solo interruttore, l'audio passa da MQTT con l'id del dispositivo
  const audio = (
    <div className="sec">
      <div className="slist">
        <div className="sline">
          <div className="grow">
            <b>Modulo vocale (Ascolta e Parla)</b>
            <span className="sub" style={{ display: "block" }}>
              Se è spento, Ascolta e Parla non compaiono nella Home e nella finestra dell'allarme.
            </span>
          </div>
          <Switch on={d.audio} label={d.audio ? "Attivo" : "Spento"} onClick={() => set({ audio: !d.audio })} />
        </div>
      </div>
      <span className="sub">Accendilo solo se il dispositivo ha il modulo vocale.</span>
    </div>
  );

  function hasSavedCam() {
    return !!s && parseCameraUrl(s.cameraUrl).ip !== "";
  }

  // ---------------- finestra ----------------

  // senza nome il titolo dice già tipo e numero
  const sub = s && hasPerson(s.patient) ? <span className="sub" style={{ fontSize: 14, fontWeight: 400 }}> {devLabel(s)}</span> : null;
  const name = s ? dispName(s) : "";
  const titleText =
    part === "new"
      ? d.kind === "Varco"
        ? "Nuovo varco"
        : "Nuovo letto"
      : part === "name"
      ? "Nome e posizione di " + name
      : part === "sensors"
      ? "Sensori di " + name
      : part === "audio"
      ? "Audio di " + name
      : "Telecamera di " + name;
  const okText = part === "new" ? (d.kind === "Varco" ? "Crea varco" : "Crea letto") : "Salva";

  // frase in fondo alla finestra di creazione: dice com'è il dispositivo appena creato
  const newNote = () => {
    const sensorsTxt = d.kind === "Varco" ? "il sensore della porta" : "i sensori del letto (alzata dx e sx, peso, rumore)";
    return (
      <Msg kind="info">
        Nasce spento, con {sensorsTxt} spenti e senza audio: si accendono con l'interruttore della riga e con le matite di Sensori e Audio.{" "}
        {filled(d.person.identifier)
          ? "Resta tra «Da completare» finché non ha almeno un operatore: si aggiunge con la matita della riga."
          : "Resta tra «Da completare» finché non ha nome e posizione e almeno un operatore."}
      </Msg>
    );
  };

  return (
    <Dialog
      title={
        <>
          {titleText}
          {sub}
        </>
      }
      ariaTitle={titleText}
      wide={part === "sensors" || part === "new" || part === "name"}
      why={why}
      onClose={() => {
        if (!saving) props.onClose();
      }}
      footer={
        <>
          <Btn text="Annulla" why={saving ? "Salvataggio in corso" : null} onClick={props.onClose} />
          <Btn text={saving ? "Salvataggio..." : okText} primary spin={saving} why={why} onClick={save} />
        </>
      }
    >
      {(part === "new" || part === "name") && (
        <>
          <div className="sec">{tipo}</div>
          {person}
        </>
      )}
      {part === "new" && newNote()}
      {part === "sensors" && sensors()}
      {part === "cam" && camera}
      {part === "audio" && audio}
      {saveErr && <Msg kind="err">{saveErr}</Msg>}
    </Dialog>
  );
};

export default DeviceDialog;
